import { Forward, Trash2, FileDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VAT_RATE = 0.15;
const fmt = (n) =>
  Number(n || 0).toLocaleString('en-ZA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const unitPrices = (item) => {
  const excl = Number(item.unit_price_excl ?? item.unit_price ?? 0);
  const incl = Number(item.unit_price_incl ?? excl * (1 + VAT_RATE));
  return { excl, incl };
};

const orderTotals = (order) => {
  let excl = Number(order.total_amount_excl ?? NaN);
  let incl = Number(order.total_amount_incl ?? NaN);
  if (!Number.isFinite(excl) || !Number.isFinite(incl)) {
    excl = 0;
    incl = 0;
    for (const it of order.items || []) {
      const { excl: e, incl: i } = unitPrices(it);
      excl += e * it.quantity;
      incl += i * it.quantity;
    }
    if (!excl && !incl && order.total_amount) {
      excl = Number(order.total_amount);
      incl = excl * (1 + VAT_RATE);
    }
  }
  return { excl, incl, vat: incl - excl };
};

const AdminOrders = ({ orders, onUpdateStatus, onForward, onDelete }) => {
  const downloadPdf = async (orderId) => {
    try {
      const response = await axios.get(`${API}/partner/orders/${orderId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `EWSS-Quote-${orderId.slice(0, 8).toUpperCase()}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download PDF');
    }
  };

  if (orders.length === 0) {
    return (
      <div className="text-center py-12 text-gray-400" data-testid="admin-orders-empty">
        No orders yet
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-orders">
      {orders.map((order) => {
        const totals = orderTotals(order);
        return (
          <div
            key={order.id}
            className="bg-black/40 backdrop-blur-sm rounded-xl p-6 border border-gray-700"
            data-testid={`admin-order-${order.id.slice(0, 8)}`}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-white">{order.partner_company}</h3>
                <p className="text-gray-400 text-sm">{order.partner_email}</p>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-400">Total (incl VAT)</div>
                <p className="text-2xl font-bold text-ewss-cyan">R {fmt(totals.incl)}</p>
                <p className="text-xs text-gray-400 mt-1">
                  (excl VAT: R {fmt(totals.excl)})
                </p>
                <p className="text-gray-400 text-xs mt-1">
                  {new Date(order.created_at).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="mb-4">
              <h4 className="text-white font-semibold mb-2">Items:</h4>
              <div className="space-y-1">
                {order.items.map((item, idx) => {
                  const { excl, incl } = unitPrices(item);
                  return (
                    <div
                      key={idx}
                      className="text-sm text-gray-300 bg-ewss-navy/30 p-2 rounded flex flex-wrap gap-x-4 gap-y-1"
                    >
                      <span className="flex-1 min-w-[200px]">
                        {item.quantity}x {item.product_name}{' '}
                        <span className="text-gray-500 text-xs">({item.sku})</span>
                      </span>
                      <span className="text-xs">
                        Unit excl <span className="text-white">R {fmt(excl)}</span> / incl{' '}
                        <span className="text-ewss-cyan">R {fmt(incl)}</span>
                      </span>
                      <span className="text-xs">
                        Line excl <span className="text-white">R {fmt(excl * item.quantity)}</span>{' '}
                        / incl{' '}
                        <span className="text-ewss-cyan font-semibold">
                          R {fmt(incl * item.quantity)}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
              {/* Totals breakdown */}
              <div className="mt-3 border-t border-gray-700 pt-2 text-sm space-y-0.5">
                <div className="flex justify-between text-gray-300">
                  <span>Subtotal (excl VAT)</span>
                  <span>R {fmt(totals.excl)}</span>
                </div>
                <div className="flex justify-between text-gray-400 text-xs">
                  <span>VAT (15%)</span>
                  <span>R {fmt(totals.vat)}</span>
                </div>
                <div className="flex justify-between font-semibold pt-1">
                  <span className="text-white">Total (incl VAT)</span>
                  <span className="text-ewss-cyan">R {fmt(totals.incl)}</span>
                </div>
              </div>
            </div>

            {order.notes && (
              <div className="mb-4 text-sm">
                <span className="text-gray-400">Notes: </span>
                <span className="text-white">{order.notes}</span>
              </div>
            )}

            <div className="flex gap-2 flex-wrap">
              <select
                value={order.status}
                onChange={(e) => onUpdateStatus(order.id, e.target.value)}
                className="px-4 py-2 bg-ewss-navy border border-gray-600 rounded-lg text-white"
                data-testid={`admin-order-status-select-${order.id.slice(0, 8)}`}
              >
                <option value="pending">Pending</option>
                <option value="quoted">Quoted</option>
                <option value="confirmed">Confirmed</option>
                <option value="shipped">Shipped</option>
                <option value="completed">Completed</option>
              </select>
              <button
                onClick={() => downloadPdf(order.id)}
                className="px-4 py-2 bg-ewss-cyan/10 border border-ewss-cyan/30 text-ewss-cyan rounded-lg font-semibold hover:bg-ewss-cyan/20 flex items-center gap-2 transition-colors"
                title="Download quotation PDF"
                data-testid={`admin-order-pdf-${order.id.slice(0, 8)}`}
              >
                <FileDown size={18} />
                PDF
              </button>
              <button
                onClick={() => onForward(order.id)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
                title="Forward to info@ewss.co.za"
                data-testid={`admin-order-forward-${order.id.slice(0, 8)}`}
              >
                <Forward size={18} />
                Forward
              </button>
              <button
                onClick={() => onDelete(order.id)}
                className="px-4 py-2 bg-red-600/80 hover:bg-red-600 text-white rounded-lg font-semibold flex items-center gap-2 transition-colors"
                title="Delete order"
                data-testid={`admin-order-delete-${order.id.slice(0, 8)}`}
              >
                <Trash2 size={18} />
                Delete
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdminOrders;

